<script>
import WidgetHead from './WidgetHead.vue';
import WidgetBody from './WidgetBody.vue';
import WidgetFooter from './WidgetFooter.vue';
import Tabs from 'dashboard/components/ui/Tabs/Tabs.vue';
import TabsItem from 'dashboard/components/ui/Tabs/TabsItem.vue';
import { useBranding } from 'shared/composables/useBranding';
import { mapGetters } from 'vuex';
import { getContrastingTextColor } from '@chatwoot/utils';
import { resolveColor } from 'shared/helpers/colorHelper';
import bubbleLogo from 'dashboard/assets/images/bubble-logo.svg';

export default {
  name: 'Widget',
  components: {
    WidgetHead,
    WidgetBody,
    WidgetFooter,
    Tabs,
    TabsItem,
  },
  props: {
    welcomeHeading: {
      type: String,
      default: '',
    },
    welcomeTagline: {
      type: String,
      default: '',
    },
    welcomeDisclaimer: {
      type: String,
      default: '',
    },
    websiteName: {
      type: String,
      required: true,
    },
    logo: {
      type: String,
      default: '',
    },
    isOnline: {
      type: Boolean,
      default: true,
    },
    replyTime: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    widgetBubblePosition: {
      type: String,
      default: '',
    },
    widgetBubbleLauncherTitle: {
      type: String,
      default: '',
    },
    widgetBubbleType: {
      type: String,
      default: '',
    },
    backgroundColor: {
      type: String,
      default: '',
    },
    widgetTheme: {
      type: Object,
      default: () => ({}),
    },
    customBubbleIcon: {
      type: String,
      default: '',
    },
    customBubbleWidth: {
      type: [Number, String],
      default: 60,
    },
    customBubbleHeight: {
      type: [Number, String],
      default: 60,
    },
    bubbleIconName: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      bubbleLogo,
    };
  },
  data() {
    return {
      activeScreenIndex: 0,
      isWidgetVisible: true,
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    getWidgetConfig() {
      return {
        welcomeHeading: this.welcomeHeading,
        welcomeTagline: this.welcomeTagline,
        welcomeDisclaimer: this.welcomeDisclaimer,
        websiteName: this.websiteName,
        logo: this.logo,
        isDefaultScreen: this.activeScreenIndex === 0,
        isOnline: this.isOnline,
        replyTime: this.replyTimeText,
        color: this.color,
      };
    },
    replyTimeText() {
      switch (this.replyTime) {
        case 'disabled':
          return '';
        case 'in_a_few_minutes':
          return this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_FEW_MINUTES'
          );
        case 'in_a_day':
          return this.$t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_DAY');
        default:
          return this.$t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_FEW_HOURS');
      }
    },
    previewThemeVars() {
      const t = this.widgetTheme || {};
      const primary = resolveColor(t.primaryColor, this.color || '#7663ab');
      const bg = resolveColor(t.backgroundColor, '#ffffff');
      const bannerBg = resolveColor(t.bannerBgColor, primary);
      const bannerText = resolveColor(
        t.bannerTextColor,
        getContrastingTextColor(bannerBg)
      );
      const bannerIconColor = resolveColor(t.bannerIconColor, bannerText);
      const userBubbleBg = resolveColor(t.userBubbleBgColor, primary);
      const userBubbleText = resolveColor(
        t.userBubbleTextColor,
        getContrastingTextColor(userBubbleBg)
      );
      const agentBubbleBg = resolveColor(t.agentBubbleBgColor, '#f4f6fb');
      const agentBubbleText = resolveColor(t.agentBubbleTextColor, '#3c4858');
      const mutedTextColor = resolveColor(t.mutedTextColor, '#3c4858');
      const inputBg = resolveColor(t.inputBgColor, '#ffffff');
      const inputText = resolveColor(t.inputTextColor, '#3c4858');
      const formFieldBg = resolveColor(t.formFieldBgColor, 'transparent');
      const borderColor = resolveColor(t.borderColor, '#e0e6ed');
      const borderRadius = t.borderRadius ? `${t.borderRadius}px` : '16px';
      const boxShadow = resolveColor(
        t.boxShadow,
        '0 8px 40px rgba(0, 0, 0, 0.16)'
      );
      const DEFAULT_FONT =
        "'MonaSans', -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
      const fontFamily = t.fontFamily
        ? `'${t.fontFamily}', ${DEFAULT_FONT}`
        : DEFAULT_FONT;
      return {
        '--wt-primary': primary,
        '--wt-bg': bg,
        '--wt-banner-bg': bannerBg,
        '--wt-banner-text': bannerText,
        '--wt-banner-icon-color': bannerIconColor,
        '--wt-user-bubble-bg': userBubbleBg,
        '--wt-user-bubble-text': userBubbleText,
        '--wt-agent-bubble-bg': agentBubbleBg,
        '--wt-agent-bubble-text': agentBubbleText,
        '--wt-muted-text': mutedTextColor,
        '--wt-input-bg': inputBg,
        '--wt-input-text': inputText,
        '--wt-form-field-bg': formFieldBg,
        '--wt-border-color': borderColor,
        '--wt-border-radius': borderRadius,
        '--wt-shadow': boxShadow,
        '--wt-font': fontFamily,
        fontFamily: fontFamily,
      };
    },
    getBubblePositionStyle() {
      if (this.widgetBubblePosition === 'center') {
        return { justifyContent: 'center' };
      }
      return {
        justifyContent: this.widgetBubblePosition === 'left' ? 'start' : 'end',
      };
    },
    isBubbleExpanded() {
      return (
        !this.isWidgetVisible && this.widgetBubbleType === 'expanded_bubble'
      );
    },
    isBarType() {
      return this.widgetBubbleType === 'bar';
    },
    isLabelType() {
      return this.widgetBubbleType === 'label';
    },
    isFlapType() {
      return this.widgetBubbleType === 'flap';
    },
    isInputBarType() {
      return this.widgetBubbleType === 'input_bar';
    },
    primaryColorValue() {
      return this.widgetTheme?.primaryColor || this.color || '#7663ab';
    },
    getWidgetBubbleLauncherTitle() {
      return this.isWidgetVisible || this.widgetBubbleType === 'standard'
        ? ' '
        : this.widgetBubbleLauncherTitle;
    },
    bubbleStyles() {
      const width = this.customBubbleWidth || 64;
      const height = this.customBubbleHeight || 64;

      if (this.isBubbleExpanded) {
        return {
          background: this.widgetTheme?.primaryColor || this.color,
          height: '48px',
          width: 'auto',
        };
      }

      // If custom icon and widget is closed (showing the icon), use transparent background
      // If custom icon and widget is open (showing close button), use widget color
      const shouldUseTransparent =
        this.customBubbleIcon && !this.isWidgetVisible;

      return {
        background: shouldUseTransparent
          ? 'transparent'
          : this.widgetTheme?.primaryColor || this.color,
        width: `${width}px`,
        height: `${height}px`,
      };
    },
    bubbleIconStyles() {
      if (this.customBubbleIcon) {
        const size = this.isBubbleExpanded ? '32px' : '100%';
        return {
          objectFit: 'cover',
          width: size,
          height: size,
        };
      }
      return {
        width: '24px',
        height: '24px',
      };
    },
  },
  methods: {
    handleScreenChange(index) {
      this.activeScreenIndex = index;
    },
    toggleWidget() {
      this.isWidgetVisible = !this.isWidgetVisible;
      this.isDefaultScreen = true;
    },
  },
};
</script>

<template>
  <div class="relative">
    <div v-if="isWidgetVisible" class="flex flex-col items-center">
      <Tabs
        :index="activeScreenIndex"
        class="px-2"
        @change="handleScreenChange"
      >
        <TabsItem
          :index="0"
          :name="$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_SCREEN.DEFAULT')"
          :show-badge="false"
        />
        <TabsItem
          :index="1"
          :name="$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_SCREEN.CHAT')"
          :show-badge="false"
        />
      </Tabs>
    </div>
    <div
      v-if="isWidgetVisible"
      class="widget-wrapper flex flex-col justify-between h-[31.25rem] w-80"
      :style="{
        ...previewThemeVars,
        background: 'var(--wt-bg)',
        boxShadow: 'var(--wt-shadow)',
        borderRadius: 'var(--wt-border-radius)',
      }"
    >
      <WidgetHead :config="getWidgetConfig" />
      <div>
        <WidgetBody
          v-if="!getWidgetConfig.isDefaultScreen"
          :config="getWidgetConfig"
        />
        <WidgetFooter :config="getWidgetConfig" />
        <div class="py-2.5 flex justify-center">
          <a
            class="branding--link cursor-pointer font-semibold inline-flex no-underline justify-center items-center text-xxs text-center mt-2 px-3 bg-slate-950/25 rounded-full gap-1"
          >
            <img
              class="ltr:mr-1 rtl:ml-1 max-w-3 max-h-3"
              :src="globalConfig.logoThumbnail"
            />
            <span className="text-white">
              {{
                replaceInstallationName(
                  $t('INBOX_MGMT.WIDGET_BUILDER.BRANDING_TEXT')
                )
              }}
            </span>
          </a>
        </div>
      </div>
    </div>
    <!-- Flap bubble preview — vertical side tab anchored to edge of preview -->
    <button
      v-if="isFlapType"
      class="absolute top-1/2 -translate-y-1/2 flex flex-col items-center justify-center w-10 h-[120px] gap-3 py-4 cursor-pointer text-white z-10"
      :class="[
        widgetBubblePosition === 'left'
          ? 'left-0 rounded-r-lg rounded-l-none'
          : 'right-0 rounded-l-lg rounded-r-none',
      ]"
      :style="{ background: primaryColorValue }"
      @click="toggleWidget"
    >
      <template v-if="!isWidgetVisible">
        <img
          v-if="customBubbleIcon"
          :src="customBubbleIcon"
          class="w-4 h-4 rounded-full object-cover flex-shrink-0"
          alt=""
          draggable="false"
        />
        <span
          v-else-if="bubbleIconName"
          :class="'i-lucide-' + bubbleIconName"
          class="text-base text-white"
        />
        <img
          v-else
          :src="bubbleLogo"
          class="w-4 h-4 flex-shrink-0"
          alt=""
          draggable="false"
        />
        <span
          class="text-base font-medium [writing-mode:vertical-rl] rotate-180"
        >
          {{ widgetBubbleLauncherTitle || 'Chat' }}
        </span>
      </template>
      <template v-else>
        <div class="relative w-3 h-3">
          <div
            class="absolute w-0.5 h-3 rotate-45 bg-white left-1/2 -translate-x-1/2"
          />
          <div
            class="absolute w-0.5 h-3 -rotate-45 bg-white left-1/2 -translate-x-1/2"
          />
        </div>
      </template>
    </button>

    <div class="relative flex mt-4 w-80" :style="getBubblePositionStyle">
      <!-- Bar bubble preview -->
      <div
        v-if="isBarType && !isWidgetVisible"
        class="flex items-center gap-3 h-14 px-4 bg-white rounded-lg shadow-md cursor-pointer max-w-full"
        @click="toggleWidget"
      >
        <img
          v-if="customBubbleIcon"
          :src="customBubbleIcon"
          class="w-8 h-8 rounded-full object-cover flex-shrink-0"
          alt=""
          draggable="false"
        />
        <span
          v-else-if="bubbleIconName"
          :class="'i-lucide-' + bubbleIconName"
          class="text-2xl text-slate-800 flex-shrink-0"
        />
        <img
          v-else
          :src="globalConfig.logoThumbnail"
          class="w-8 h-8 flex-shrink-0"
          alt=""
          draggable="false"
        />
        <span class="text-sm font-medium text-slate-800 truncate">
          {{ widgetBubbleLauncherTitle || 'create your experience' }}
        </span>
      </div>
      <!-- Bar close button -->
      <button
        v-if="isBarType && isWidgetVisible"
        class="flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-md cursor-pointer hover:bg-slate-100"
        @click="toggleWidget"
      >
        <div class="relative w-3.5 h-3.5">
          <div
            class="absolute w-0.5 h-3.5 rotate-45 bg-slate-600 left-1/2 -translate-x-1/2"
          />
          <div
            class="absolute w-0.5 h-3.5 -rotate-45 bg-slate-600 left-1/2 -translate-x-1/2"
          />
        </div>
      </button>

      <!-- Label bubble preview — bottom-anchored tab -->
      <button
        v-else-if="isLabelType"
        class="flex items-center gap-2 h-10 px-4 rounded-t-lg rounded-b-none cursor-pointer text-white text-base font-medium"
        :style="{ background: primaryColorValue }"
        @click="toggleWidget"
      >
        <template v-if="!isWidgetVisible">
          <img
            v-if="customBubbleIcon"
            :src="customBubbleIcon"
            class="w-4 h-4 rounded-full object-cover flex-shrink-0"
            alt=""
            draggable="false"
          />
          <span
            v-else-if="bubbleIconName"
            :class="'i-lucide-' + bubbleIconName"
            class="text-base text-white"
          />
          <img
            v-else
            :src="bubbleLogo"
            class="w-4 h-4 flex-shrink-0"
            alt=""
            draggable="false"
          />
          <span>{{ widgetBubbleLauncherTitle || 'Chat with us' }}</span>
        </template>
        <template v-else>
          <div class="relative w-3 h-3">
            <div
              class="absolute w-0.5 h-3 rotate-45 bg-white left-1/2 -translate-x-1/2"
            />
            <div
              class="absolute w-0.5 h-3 -rotate-45 bg-white left-1/2 -translate-x-1/2"
            />
          </div>
        </template>
      </button>

      <!-- Input bar bubble preview — centered pill with placeholder -->
      <div
        v-else-if="isInputBarType && !isWidgetVisible"
        class="flex items-center gap-2.5 w-full max-w-[280px] min-h-[52px] px-5 py-3.5 bg-white border border-slate-200 rounded-[26px] shadow-md cursor-pointer"
        @click="toggleWidget"
      >
        <span
          class="i-lucide-message-circle text-xl text-slate-400 flex-shrink-0"
        />
        <span class="text-sm text-slate-400">
          {{ widgetBubbleLauncherTitle || 'Ask a question…' }}
        </span>
      </div>

      <!-- Standard / expanded bubble preview -->
      <button
        v-else-if="!isBarType && !isLabelType && !isFlapType && !isInputBarType"
        class="relative flex items-center justify-center rounded-full cursor-pointer transition-transform hover:scale-110"
        :style="bubbleStyles"
        :class="
          isBubbleExpanded
            ? 'w-auto font-medium text-base text-white dark:text-white h-12 px-4'
            : ''
        "
        @click="toggleWidget"
      >
        <img
          v-if="!isWidgetVisible && customBubbleIcon"
          :src="customBubbleIcon"
          alt="Custom bubble icon"
          draggable="false"
          :style="bubbleIconStyles"
          class="rounded-full flex-shrink-0"
        />
        <span
          v-else-if="!isWidgetVisible && bubbleIconName"
          :class="'i-lucide-' + bubbleIconName"
          class="text-2xl text-white"
        />
        <img
          v-else-if="!isWidgetVisible"
          :src="bubbleLogo"
          alt=""
          draggable="false"
          :style="bubbleIconStyles"
          class="mx-auto"
        />
        <div v-if="isBubbleExpanded" class="ltr:pl-2.5 rtl:pr-2.5">
          {{ getWidgetBubbleLauncherTitle }}
        </div>
        <div v-if="isWidgetVisible" class="relative">
          <div class="absolute w-0.5 h-8 rotate-45 -translate-y-1/2 bg-white" />
          <div
            class="absolute w-0.5 h-8 -rotate-45 -translate-y-1/2 bg-white"
          />
        </div>
      </button>
    </div>
  </div>
</template>
